import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import Navigation from "../components/Navigation";
import AssessmentStatusBadge from "../utils/assessmentStatusLabel";
import Card from '../components/Card';
import Button from '../components/Buttons';
import LoadingSpinner from '../components/LoadingSpinner';
import { Plus, FileText, Shield, Trash2, RefreshCw, Upload, BarChart2, Users, Lock, Eye } from 'lucide-react';
import Pagination from "../components/Pagination";
import { usePageSize } from "../hooks/usePageSize";
import { useVerticals } from "../contexts/VerticalsContext";
import TPRMVendorSelectModal from "../components/TPRMVendorSelectModal";
import TPRMRespondentsModal from "../components/TPRMRespondentsModal";
import DealAssignAssessModal from "../components/DealAssignAssessModal";
import AssessmentUnassignedModal from "../components/AssessmentUnassignedModal";
import { useRBAC } from "../hooks/useRBAC";
import { useURLState } from "../hooks/useURLState";
import CreateDealModal from './CreateDealModal';
import DeleteAssessmentModal from './DeleteAssessmentModal';
import ReassessModal from './ReassessModal';
import AssessmentActivityLog from "../components/AssessmentActivityLog";

import { SHOW_DELETE_ASSESSMENT } from '../config';
const TPRMDeals = () => {
  const [deals, setdeals] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showNewVendorModal, setShowNewApplicationModal] = useState(false);
  const [showNewDealModal, setShowNewDealModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [creatingAssessment, setCreatingAssessment] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedEntityForAssign, setSelectedEntityForAssign] = useState(null);
  const [currentPageParam, setCurrentPageParam] = useURLState('page');
  const currentPage = parseInt(currentPageParam) || 1;
  const [dealIdParam, setDealIdParam] = useURLState('dealId');
  const [totalCount, setTotalCount] = useState(0);
  const [paging, setPaging] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showReassessModal, setShowReassessModal] = useState(false);
  const [assessmentToReassess, setAssessmentToReassess] = useState(null);
  const [reassessing, setReassessing] = useState(false);

  const [showUnassignedAlert, setShowUnassignedAlert] = useState(false);
  const [respondentsAssessmentId, setRespondentsAssessmentId] = useState(null);

  const [pageSize, setPageSize] = usePageSize('tprmDeals');
  const totalPages = Math.ceil(totalCount / pageSize);
  const listTopRef = useRef(null);

  const rightPanelRef = useRef(null);
  const fetchIdRef = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();
  const apiCall = useApi();
  const { user } = useAuth();
  const { canAssess, getAssessDisabledReason, canManageOrganizationRisks, canManageDeal, canViewDeal, canAssessViaAssignment, canCreateDealOrBusinessUnit, authVersion, canDeleteAssessments } = useRBAC();
  const canDeleteAssessment = SHOW_DELETE_ASSESSMENT && canDeleteAssessments(); // super admin only, and the switch in config.js
  const selectedOrg = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  const canManageRisks = canManageOrganizationRisks();
  const canManageRisksForDeal = (dealID) => canManageRisks || canManageDeal(dealID);
  const canAddDealOrBU = canCreateDealOrBusinessUnit(selectedOrg?.organizationId);

  const { verticals } = useVerticals();

  // Reset assessment state when user/auth changes
  useEffect(() => {
    setAssessments([]);
    setSelectedDeal(null);
  }, [authVersion]);

  useEffect(() => {
    fetchdeals();
    fetchVendors();
  }, [authVersion, currentPage, pageSize]);

  const fetchVendors = async () => {
    try {
      const data = await apiCall("GET", "/TPRMVendor/vendors?pageSize=100");
      setVendors(data.items || []);
    } catch (err) {
      setVendors([]);
    }
  };




  // Auto-select from URL param or navigation state
  useEffect(() => {
    if (deals.length === 0) return;

    // Prefer URL param, fall back to navigation state
    const autoSelectId = dealIdParam || location.state?.selecteddealID;
    if (autoSelectId) {
      const targetId = typeof autoSelectId === 'string' ? parseInt(autoSelectId) : autoSelectId;
      const target = deals.find(a => a.dealID === targetId);
      if (target) {
        setSelectedDeal(target);
        fetchDealAssessments(target.dealID);
      }
      if (location.state?.selecteddealID) {
        navigate('/TPRMDeals', { replace: true });
      }
    }
  }, [deals]);

  const fetchdeals = async () => {
    setPaging(true);
    try {
      const org = JSON.parse(localStorage.getItem("selectedOrganization"));
      if (!org?.organizationId) return;

      const data = await apiCall("GET", `/Deals/summary?category=Vendor&page=${currentPage}&pageSize=${pageSize}`);

      const dealsWithRisk = (data.items || []).map((deal) => ({
        dealID: deal.dealId,
        isPrivate: deal.isPrivate,
        dealName: deal.dealName,
        dealOwner: deal.dealOwner,
        dealStatus: deal.dealStatus,
        assessmentCount: deal.applicationCount || 0,
        RiskLevel: deal.riskLevel === "NONE" ? "No RISK" : (deal.riskLevel || "No RISK"),
        riskCounts: {
          CRITICAL: deal.riskCounts?.critical || 0,
          HIGH: deal.riskCounts?.high || 0,
          MEDIUM: deal.riskCounts?.medium || 0,
          LOW: deal.riskCounts?.low || 0,
        },
        verticalId: deal.verticalId,
        TargetEndDate: deal.targetEndDate,
        domain: deal.domain,
      }));

      // Backend /Deals/summary already applies RBAC deal-scoping and returns a scoped
      // totalCount, so trust the server response directly. Re-filtering client-side broke
      // pagination for deal-scoped users by clobbering totalCount with the current page's length.
      setdeals(dealsWithRisk);
      setTotalCount(data.totalCount || 0);
    } catch (error) {} finally {
      setLoading(false);
      setPaging(false);
    }
  };


  // Fetch deal-level assessments with pre-aggregated risk data
  const fetchDealAssessments = async (dealId) => {
    const thisFetchId = ++fetchIdRef.current;
    setAssessmentsLoading(true);
    try {
      const data = await apiCall("GET", `/TPRMAssessments/summary?dealId=${dealId}`);
      if (thisFetchId !== fetchIdRef.current) return;

      const normalized = (Array.isArray(data) ? data : []).map((a) => ({
        ...a,
        hasRisks: a.totalRisks > 0,
        riskCounts: {
          CRITICAL: a.riskCounts?.critical || 0,
          HIGH: a.riskCounts?.high || 0,
          MEDIUM: a.riskCounts?.medium || 0,
          LOW: a.riskCounts?.low || 0,
        },
      }));

      setAssessments(normalized);
    } catch (err) {
      if (thisFetchId !== fetchIdRef.current) return;
    } finally {
      if (thisFetchId === fetchIdRef.current) {
        setAssessmentsLoading(false);
      }
    }
  };

  // Handle vendor creation with auto-assessment
  const handleVendorCreatedWithAssessment = async (newVendor) => {
    setCreatingAssessment(true);
    try {
      const org = JSON.parse(localStorage.getItem("selectedOrganization"));

      // Create deal-level TPRM Assessment (WITH DealId)
      const assessmentPayload = {
        DealId: selectedDeal.dealID,
        OrganizationId: org.organizationId,
        VendorId: newVendor.vendorId,
        RegionId: newVendor.regionId || null,
        VerticalId: newVendor.verticalId || null,
        TPRMAssessmentType: "Initial",
        TPRMFramework: "Default",
        TPRMAssessmentDate: new Date().toISOString(),
        TPRMStatus: "in_progress",
        TPRMProgress: 0,
      };

      await apiCall("POST", "/TPRMAssessments", assessmentPayload);
      setShowNewApplicationModal(false);
      fetchDealAssessments(selectedDeal.dealID);
      fetchdeals();
    } catch (err) {
      alert("Vendor created, but assessment creation failed. You can assign manually.");
      setShowNewApplicationModal(false);
      fetchDealAssessments(selectedDeal.dealID);
      fetchdeals();
    } finally {
      setCreatingAssessment(false);
    }
  };


  const handleDeleteAssessment = async () => {
    if (!assessmentToDelete) return;
    setDeleting(true);
    try {
      await apiCall('DELETE', `/tprmAssessments/${assessmentToDelete}`);
      setShowDeleteModal(false);
      setAssessmentToDelete(null);
      await fetchDealAssessments(selectedDeal.dealID);
    } catch (error) {} finally {
      setDeleting(false);
    }
  };

  const handleReassessAssessment = async () => {
    if (!assessmentToReassess) return;
    setReassessing(true);
    try {
        await apiCall('POST', `/tprmAssessments/${assessmentToReassess}/reassess`);
        setShowReassessModal(false);
        setAssessmentToReassess(null);
        navigate(`/TPRMAssessment/${assessmentToReassess}?step=review`);
    } catch (error) {} finally {
        setReassessing(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />

      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-content-primary">Deal Vendor Management</h1>
          <p className="mt-2 text-gray-600">
            Manage Deal Vendors for security risk assessment
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[43fr_57fr] gap-8">
          {/* Deals */}
          <Card>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Deals</h2>
              {canAddDealOrBU && (
                <Button size="sm" onClick={() => setShowNewDealModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Deal
                </Button>
              )}
            </div>

            <div className="space-y-3" ref={listTopRef}>
              {deals.map((deal) => (
                <div
                  key={deal.dealID}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${selectedDeal?.id === deal.dealID
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                    }`}
                  onClick={() => {
                    setSelectedDeal(deal);
                    setDealIdParam(deal.dealID);
                    fetchDealAssessments(deal.dealID);
                    rightPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-medium text-content-primary">{deal.dealName}{deal.isPrivate && <span className="inline-flex items-center gap-1 ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded align-middle"><Lock className="h-3 w-3" />Private</span>}</h3>
                      <p className="text-sm text-gray-600">{verticals.find(v => v.verticalId === deal.verticalId)?.verticalName || "N/A"}</p>
                      {deal.description && (
                        <p className="text-sm text-gray-500 mt-1">{deal.description}</p>
                      )}
                      {deal.TargetEndDate && (
                        <p className="text-xs text-gray-500 mt-1">
                          Due: {new Date(deal.TargetEndDate).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <div className="ml-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          {deal.assessmentCount || 0} Vendors
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Risk Bar */}
                  <div className="mt-3">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-gray-600">Risk Level</span>
                      <span className="text-xs font-medium text-content-primary">
                        {deal.RiskLevel || 'No RISK'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${deal.RiskLevel === 'CRITICAL'
                          ? 'bg-red-700'
                          : deal.RiskLevel === 'HIGH'
                            ? 'bg-orange-500'
                            : deal.RiskLevel === 'MEDIUM'
                              ? 'bg-yellow-500'
                              : deal.RiskLevel === 'LOW'
                                ? 'bg-green-500'
                                : 'bg-gray-400'
                          }`}
                        style={{
                          width: `${deal.RiskLevel === 'CRITICAL'
                            ? 90
                            : deal.RiskLevel === 'HIGH'
                              ? 75
                              : deal.RiskLevel === 'MEDIUM'
                                ? 50
                                : deal.RiskLevel === 'LOW'
                                  ? 25
                                  : 0
                            }%`,
                        }}
                      ></div>
                    </div>
                  </div>

                  {/* Risk Count Badges */}
                  {(deal.riskCounts?.CRITICAL > 0 || deal.riskCounts?.HIGH > 0 || deal.riskCounts?.MEDIUM > 0 || deal.riskCounts?.LOW > 0) && (
                    <div className="flex items-center mt-2 space-x-2">
                      {deal.riskCounts.CRITICAL > 0 && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded">
                          {deal.riskCounts.CRITICAL} Critical
                        </span>
                      )}
                      {deal.riskCounts.HIGH > 0 && (
                        <span className="px-2 py-0.5 bg-orange-100 text-orange-800 text-xs rounded">
                          {deal.riskCounts.HIGH} High
                        </span>
                      )}
                      {deal.riskCounts.MEDIUM > 0 && (
                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">
                          {deal.riskCounts.MEDIUM} Medium
                        </span>
                      )}
                      {deal.riskCounts.LOW > 0 && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded">
                          {deal.riskCounts.LOW} Low
                        </span>
                      )}
                    </div>
                  )}

                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              totalItems={totalCount}
              pageSize={pageSize}
              onPageChange={setCurrentPageParam}
              onPageSizeChange={setPageSize}
              scrollTargetRef={listTopRef}
              busy={paging}
            />
          </Card>

          {/* Vendor Assessments */}
          <Card ref={rightPanelRef}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Vendor Assessments</h2>
                <div className="flex items-center gap-2">
                 {selectedDeal && assessments.length > 0 && (
                   <Button
                     size="sm"
                     variant="secondary"
                     onClick={() => navigate(`/TPRMPortfolio?dealId=${selectedDeal.dealID}`)}
                   >
                     <BarChart2 className="h-4 w-4 mr-2" />
                     Portfolio Report
                   </Button>
                 )}
                 {selectedDeal && canAddDealOrBU && (
                   <Button
                     size="sm"
                     onClick={() => setShowNewApplicationModal(true)}
                     disabled={creatingAssessment}
                   >
                     <Plus className="h-4 w-4 mr-2" />
                     {creatingAssessment ? "Creating..." : "New Vendor"}
                   </Button>
                 )}
              </div>
            </div>

            {selectedDeal ? (
              assessmentsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : assessments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No vendor assessments associated with this deal.
                </div>
              ) : (
              <div className="space-y-3">
                {[...assessments].sort((a, b) =>
                    (a.vendorName || '').localeCompare(b.vendorName || '')
                  ).map((assessment) => (
                    <div
                      key={assessment.tprmAssessmentID}
                      className="p-4 border border-gray-200 rounded-lg hover:border-gray-300"
                    >
                      <div>
                        <h3 className="font-medium text-content-primary">
                          {assessment.vendorName || `Vendor ID: ${assessment.vendorId}`}
                          <AssessmentStatusBadge status={assessment.tprmStatus} />
                        </h3>
                        <p className="text-sm text-gray-500">
                          {assessment.vendorDomain || ""}
                        </p>
                        {assessment.tprmAssignedToUserName && (
                          <p className="text-xs text-blue-600 mt-1">
                            Assigned to: <span className="font-semibold">{assessment.tprmAssignedToUserName}</span>
                          </p>
                        )}
                      </div>

                      <div className="flex gap-4 items-start">
                        <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mt-3">
                        {canManageRisksForDeal(selectedDeal?.dealID) && (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={assessment.tprmStatus === "completed"}
                            title={assessment.tprmStatus === "completed" ? "Assessment is completed" : ""}
                            onClick={() => {
                              setSelectedEntityForAssign(assessment);
                              setShowAssignModal(true);
                            }}
                          >
                            <Shield className="h-3 w-3 mr-1" />
                            {assessment.tprmAssignedToUserId ? "Reassign" : "Assign"}
                          </Button>
                        )}

                        {canManageRisksForDeal(selectedDeal?.dealID) && assessment.tprmAssessmentID && (
                          <Button
                            size="sm"
                            variant="secondary"
                            className="whitespace-nowrap"
                            title="Manage additional respondents for this assessment"
                            onClick={() => setRespondentsAssessmentId(assessment.tprmAssessmentID)}
                          >
                            <Users className="h-4 w-4 mr-1" />
                            Respondents
                          </Button>
                        )}

                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={
                            !canAssess(assessment.tprmAssignedToUserId, null, 'DEAL', selectedDeal?.dealID, assessment.assigneeUserIds || []) ||
                            assessment.tprmStatus === "completed" ||
                            assessment.tprmStatus === "analyzing"
                          }
                          title={
                            (assessment.tprmStatus === "completed" ? "Assessment is completed"
                              : assessment.tprmStatus === "analyzing" ? "Assessment is being analyzed"
                              : "")
                              || getAssessDisabledReason(assessment.tprmAssignedToUserId, null, 'DEAL', selectedDeal?.dealID, assessment.assigneeUserIds || [])
                              || ""
                          }
                          onClick={() => {
                            if (!assessment.tprmAssignedToUserId && !(assessment.assigneeUserIds || []).length) {
                              setShowUnassignedAlert(true);
                              return;
                            }
                            // Get vendorId from assessment, if 0 then from vendor object
                            const vendor = vendors.find(v => v.vendorId === assessment.vendorId);
                            const vendorId = assessment.vendorId || vendor?.vendorId || 0;

                            if (!vendorId || vendorId === 0) {
                              alert("No vendor associated. Please create a new assessment.");
                              return;
                            }

                            const tprmPath = assessment.tprmAssessmentID
                              ? `/TPRMAssessment/${assessment.tprmAssessmentID}`
                              : '/TPRMAssessment';
                            navigate(tprmPath, {
                              state: {
                                vendorId: vendorId,
                                vendorName: assessment.vendorName || vendor?.vendorName,
                                assessmentId: assessment.tprmAssessmentID,
                                dealID: selectedDeal.dealID,
                                dealName: selectedDeal.dealName,
                                verticalId: vendor?.verticalId || selectedDeal.verticalId,
                                regionId: vendor?.regionId || selectedDeal.regionId,
                                subVerticalId: vendor?.subVerticalId || selectedDeal.subVerticalId,
                              }
                            });
                          }}
                        >
                          <FileText className="h-4 w-4 mr-1" />
                          Assess
                        </Button>

                        {/* Viewing mirrors the backend's GetViewableAssessmentAsync: deal scope or assignment. */}
                        {assessment.tprmAssessmentID && assessment.tprmStatus === "completed" &&
                          (canViewDeal(selectedDeal?.dealID) || canAssessViaAssignment(assessment.tprmAssignedToUserId, assessment.assigneeUserIds || [])) && (
                          <Button
                            size="sm"
                            variant="secondary"
                            title="View the completed assessment's questions, answers, notes, and attachments (read-only)"
                            onClick={() => navigate(`/TPRMAssessment/${assessment.tprmAssessmentID}?step=review`)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Answers
                          </Button>
                        )}

                        <Button
                          size="sm"
                          variant="secondary"
                          className="whitespace-nowrap"
                          title="Upload a vendor document for AI risk analysis"
                          onClick={() => {
                            const v = vendors.find(vnd => vnd.vendorId === assessment.vendorId);
                            const vendorId = assessment.vendorId || v?.vendorId || 0;
                            navigate('/TPRMDocumentAssessment', {
                              state: {
                                vendorId,
                                vendorName: assessment.vendorName || v?.vendorName,
                                dealId: selectedDeal.dealID,
                              }
                            });
                          }}
                        >
                          <Upload className="h-4 w-4 mr-1" />
                          Doc
                        </Button>

                        {assessment.hasRisks ? (
                          <Button
                            size="sm"
                            className="whitespace-nowrap"
                            onClick={() => navigate(`/VendorDashboard/${assessment.vendorId}`)}
                          >
                            <Shield className="h-4 w-4 mr-1" />
                            View Risks ({assessment.activeRisks ?? assessment.totalRisks})
                          </Button>
                        ) : (
                          <Button size="sm" disabled className="whitespace-nowrap">
                            <Shield className="h-4 w-4 mr-1" />
                            No Risks
                          </Button>
                        )}
                        {canManageRisksForDeal(selectedDeal?.dealID) && assessment.tprmAssessmentID && (
                          <Button size="sm" variant="warning" className="whitespace-nowrap"
                            disabled={assessment.tprmStatus !== 'completed'}
                            onClick={(e) => {
                              e.stopPropagation();
                              setAssessmentToReassess(assessment.tprmAssessmentID);
                              setShowReassessModal(true);
                            }}>
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Reassess
                          </Button>
                        )}
                        {canDeleteAssessment && assessment.tprmAssessmentID && (
                          <Button size="sm" variant="danger" className="whitespace-nowrap"
                            onClick={(e) => { e.stopPropagation(); setAssessmentToDelete(assessment.tprmAssessmentID); setShowDeleteModal(true); }}>
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>

                      {assessment.hasRisks ? (
                        <div className="mt-3">
                          {/* Risk badges */}
                          <div className="flex flex-wrap gap-2">
                            {assessment.riskCounts?.CRITICAL > 0 && (
                              <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                                {assessment.riskCounts.CRITICAL} Critical
                              </span>
                            )}
                            {assessment.riskCounts?.HIGH > 0 && (
                              <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">
                                {assessment.riskCounts.HIGH} High
                              </span>
                            )}
                            {assessment.riskCounts?.MEDIUM > 0 && (
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                                {assessment.riskCounts.MEDIUM} Medium
                              </span>
                            )}
                            {assessment.riskCounts?.LOW > 0 && (
                              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                {assessment.riskCounts.LOW} Low
                              </span>
                            )}
                          </div>

                          {/* Risk Score Bar */}
                          <div className="mt-2">
                            <div className="text-xs text-gray-500 mb-1">
                              Risk Score: {assessment.riskScore || 0}%
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-4">
                              <div
                                className={`h-4 rounded-full ${
                                  (assessment.riskScore || 0) <= 40
                                    ? 'bg-green-500'
                                    : (assessment.riskScore || 0) <= 60
                                    ? 'bg-yellow-500'
                                    : (assessment.riskScore || 0) <= 80
                                    ? 'bg-orange-500'
                                    : 'bg-red-500'
                                }`}
                                style={{ width: `${assessment.riskScore || 0}%` }}
                              />
                            </div>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {assessment.activeRisks ?? assessment.totalRisks ?? 0} Active Risks
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3 text-xs text-gray-500">No Risk Data</div>
                      )}
                        </div>
                      </div>
                      <div className="flex justify-end mt-2">
                        <AssessmentActivityLog assessmentType="tprm" assessmentId={String(assessment.tprmAssessmentID)} hideQuestionEvents />
                      </div>
                    </div>
                  ))
                }
              </div>
              )
            ) : (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Select a deal to view assessments</p>
              </div>
            )}
          </Card>
        </div>
      </div >

      {/* Vendor Select Modal */}
      <TPRMVendorSelectModal
        isOpen={showNewVendorModal}
        onClose={() => setShowNewApplicationModal(false)}
        onVendorSelected={handleVendorCreatedWithAssessment}
        vendors={vendors}
        existingVendorIds={assessments.map(a => a.vendorId)}
        onVendorCreated={(newVendor) => setVendors(prev => [...prev, newVendor])}
        dealId={selectedDeal?.dealID}
      />

      {/* Assign Assessment Modal */}
      {showAssignModal && selectedEntityForAssign && (
        <DealAssignAssessModal
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          dealID={selectedDeal?.dealID}
          isPrivate={selectedDeal?.isPrivate}
          assessmentType="tprm"
          organizationId={selectedOrg?.organizationId}
          hasExistingAssessment={true}
          assessmentId={selectedEntityForAssign?.tprmAssessmentID}
          refreshData={() => fetchDealAssessments(selectedDeal.dealID)}
        />
      )}

      <TPRMRespondentsModal
        isOpen={respondentsAssessmentId != null}
        onClose={() => setRespondentsAssessmentId(null)}
        assessmentId={respondentsAssessmentId}
        refreshData={() => fetchDealAssessments(selectedDeal?.dealID)}
      />

      {/* Create Deal Modal */}
      <CreateDealModal
        isOpen={showNewDealModal}
        onClose={() => setShowNewDealModal(false)}
        onDealCreated={() => { setCurrentPageParam(1); fetchdeals(); }}
        dealCategory="Vendor"
        title="Create New Deal"
      />

      <DeleteAssessmentModal isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setAssessmentToDelete(null); }}
        onConfirm={handleDeleteAssessment} isDeleting={deleting} />

      <ReassessModal
        isOpen={showReassessModal}
        onClose={() => { setShowReassessModal(false); setAssessmentToReassess(null); }}
        onConfirm={handleReassessAssessment}
        isReassessing={reassessing}
      />

      <AssessmentUnassignedModal
        isOpen={showUnassignedAlert}
        onClose={() => setShowUnassignedAlert(false)}
      />
    </div >
  );
};

export default TPRMDeals;
